import { WorkCycle } from '@/core/collections/contracts-api/customers/work-cycles/work-cycle.interface';
import { Worksheet } from '@/core/collections/contracts-api/customers/employees/contract-extensions/contract-extension.interface';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';
import { ValidationError } from 'yup';

export function useWorkCycleSchema() {
   const { t } = useI18n();

   const WORK_CYCLE_SCHEMA = yup.object().shape({
      name: yup.string().required().label(t('work-cycles.work-cycle', 1)),
      worksheets: yup
         .array()
         .min(2)
         .required()
         .test('worksheet-invoice-groups', '', function validateWorksheetInvoiceGroups(value) {
            const worksheets = value || [];
            const innerErrors: ValidationError[] = [];
            const requiredMessage = t('generic.feedback.required');

            if (worksheets.length < 2) {
               innerErrors.push(new ValidationError(requiredMessage, worksheets, 'work_cycle_length'));
            }

            worksheets.forEach((worksheet: Worksheet, worksheetIndex: number) => {
               if (!worksheet?.invoice_group_id) {
                  innerErrors.push(new ValidationError(requiredMessage, worksheet, `worksheets[${worksheetIndex}].invoice_group_id`));
               }
            });

            if (innerErrors.length) {
               throw new ValidationError(innerErrors);
            }

            return true;
         }),
   });

   return { WORK_CYCLE_SCHEMA };
}

export function getDefaultWorkCycleFormData() {
   const timetables = [...Array(7)].map((_, index) => ({ weekday: index + 1 }));
   return {
      worksheets: [{ id: new Date().getTime(), timetables }],
   } as Partial<WorkCycle>;
}

export function mapWorkCycleFormErrors(errors: { [key: string]: string }) {
   const mapped = { ...errors };

   for (const [path, message] of Object.entries(errors)) {
      const match = path.match(/^worksheets\[(\d+)\]\.invoice_group_id$/);
      if (match) {
         mapped[`invoice_group_id-${match[1]}`] = message;
      }
   }

   return mapped;
}

export function cloneWorkCycleForForm(workCycle: WorkCycle) {
   return JSON.parse(JSON.stringify(workCycle)) as WorkCycle;
}

export function mapWorkCyclePayloadForSave(workCycle: WorkCycle, formatTimetable: (timetable: any) => any) {
   return {
      ...workCycle,
      worksheets: workCycle.worksheets?.map((worksheet, index) => ({
         ...worksheet,
         sequence_number: index + 1,
         next_to_extend: 0,
         timetables: worksheet.timetables.filter((timetable) => timetable.planned_hours).map(formatTimetable),
      })),
   };
}
